// Colour system for the fan page, derived from the club's primary_color.
//
// The page keeps the club's brand colour as its full-bleed background — clubs
// want their identity on the page — so everything sitting on top has to be
// computed from it rather than hard-coded. The old approach painted item cards
// as a flat 5% white overlay, which measured 1.03–1.14:1 against the background
// for every club in production: an edge the eye reads as no edge at all.
//
// The rule here is that a surface moves AWAY from its text colour. That matters
// more than it sounds: lightening a dark green card until its edge is visible
// drops white text on it to 3.5:1, while deepening it buys the same edge and
// takes text up to 11:1. Where there is nowhere further to go — a black brand
// cannot get darker — it flips automatically.

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function normalizeHex(input: string | null | undefined, fallback = '#1e3a5f'): string {
  if (!input || !HEX.test(input.trim())) return fallback;
  const s = input.trim().replace('#', '');
  return '#' + (s.length === 3 ? s.split('').map(c => c + c).join('') : s).toLowerCase();
}

function toRgb(hex: string): [number, number, number] {
  const s = normalizeHex(hex).slice(1);
  return [0, 2, 4].map(i => parseInt(s.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
      .join('')
  );
}

export function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = toRgb(hex).map(v => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s * 100, l * 100];
}

export function fromHsl(h: number, s: number, l: number): string {
  const sN = Math.max(0, Math.min(100, s)) / 100;
  const lN = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;
  const t =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return toHex((t[0] + m) * 255, (t[1] + m) * 255, (t[2] + m) * 255);
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map(v => v / 255);
  const f = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Composite a white or black overlay at `alpha` over an opaque colour. */
export function composite(bg: string, white: boolean, alpha: number): string {
  const [r, g, b] = toRgb(bg);
  const t = white ? 255 : 0;
  return toHex(r * (1 - alpha) + t * alpha, g * (1 - alpha) + t * alpha, b * (1 - alpha) + t * alpha);
}

/** White or near-black, whichever is legible on `bg`. */
export function inkOn(bg: string): string {
  return contrastRatio(bg, '#ffffff') >= 4.5 ? '#ffffff' : '#0f1216';
}

/** Smallest alpha in one direction reaching `target` contrast, or null. */
function reach(bg: string, white: boolean, target: number): number | null {
  if (contrastRatio(composite(bg, white, 1), bg) < target) return null;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(composite(bg, white, mid), bg) < target) lo = mid;
    else hi = mid;
  }
  return hi;
}

/**
 * The overlay that puts a surface `target` contrast from `bg`, moving away
 * from `ink` so separation and readability improve together. Flips direction
 * when the preferred one can't reach the target.
 */
export function solveOverlay(
  bg: string,
  ink: string,
  target: number,
): { white: boolean; alpha: number; flipped: boolean } {
  const away = luminance(ink) <= 0.5;
  const preferred = reach(bg, away, target);
  if (preferred !== null) return { white: away, alpha: preferred, flipped: false };
  const other = reach(bg, !away, target);
  return { white: !away, alpha: other ?? 1, flipped: true };
}

/**
 * Walk a colour along its own hue until it is legible on `bg`. Used for the
 * home-list icon glyphs, which are painted in the brand colour on a white
 * pill — a light brand (Aris F.C.'s #c1c100) lands at 1.9:1 untreated.
 */
export function legibleOn(hex: string, bg: string, min = 4.5): string {
  const [h, s, l0] = toHsl(hex);
  if (s < 8) return inkOn(bg) === '#ffffff' ? '#f2f4f7' : '#16181d';
  const lighten = luminance(bg) < 0.4;
  for (let step = 0; step <= 100; step++) {
    const l = lighten ? Math.min(97, l0 + step) : Math.max(3, l0 - step);
    const candidate = fromHsl(h, s, l);
    if (contrastRatio(candidate, bg) >= min) return candidate;
  }
  return inkOn(bg);
}

const rgba = (white: boolean, a: number) =>
  `rgba(${white ? '255,255,255' : '0,0,0'},${a.toFixed(3)})`;

/** Target separation between an item card and the brand background. */
const CARD_SEPARATION = 1.55;

export interface FanPalette {
  /** The club's brand colour — the page background, unchanged. */
  bg: string;
  /** Translucent card fill, for use over `bg`. */
  cardBg: string;
  /** What `cardBg` resolves to over `bg` — the value contrast is measured on. */
  cardSolid: string;
  cardBorder: string;
  cardShadow: string;
  /** Text on a card. */
  ink: string;
  inkSoft: string;
  /** Image-placeholder tile inside a card. */
  tileBg: string;
  tileInk: string;
  /** Add button / cart bar. Neutral: a brand-coloured accent is invisible on a
   *  background of that same brand colour. */
  accent: string;
  accentInk: string;
  /** Text directly on the brand background (page titles, section labels). */
  pageInk: string;
  pageInkSoft: string;
  /** Home-list icon chip: brand wash, with a glyph walked to legibility. */
  iconChipBg: string;
  iconInk: string;
}

export function buildFanPalette(primaryColor: string | null | undefined): FanPalette {
  const bg = normalizeHex(primaryColor);
  const pageInk = inkOn(bg);

  const { white, alpha } = solveOverlay(bg, pageInk, CARD_SEPARATION);
  const cardSolid = composite(bg, white, alpha);
  // Text is chosen from the CARD, not the page: a card pushed far enough
  // crosses the point where the page's own text colour stops working on it.
  const ink = inkOn(cardSolid);
  const onDarkCard = ink === '#ffffff';
  // The lit edge always runs opposite the card, so a deeper card still catches
  // light along its border.
  const edgeWhite = luminance(cardSolid) < 0.5;

  const iconChipSolid = composite(bg, true, 0.88);

  return {
    bg,
    cardBg: rgba(white, alpha),
    cardSolid,
    cardBorder: rgba(edgeWhite, 0.2),
    cardShadow: '0 10px 22px -14px rgba(0,0,0,0.5)',
    ink,
    inkSoft: onDarkCard ? 'rgba(255,255,255,0.64)' : 'rgba(15,18,22,0.62)',
    tileBg: rgba(edgeWhite, Math.min(0.9, 0.1 + alpha * 0.35)),
    tileInk: onDarkCard ? 'rgba(255,255,255,0.82)' : 'rgba(15,18,22,0.75)',
    accent: pageInk === '#ffffff' ? '#ffffff' : '#101319',
    accentInk: pageInk === '#ffffff' ? '#14171c' : '#ffffff',
    pageInk,
    pageInkSoft: pageInk === '#ffffff' ? 'rgba(255,255,255,0.7)' : 'rgba(15,18,22,0.66)',
    iconChipBg: `color-mix(in srgb, ${bg} 12%, #ffffff)`,
    iconInk: legibleOn(bg, iconChipSolid, 4.5),
  };
}
